import { useEffect, useState } from 'react';
import { Plus, Link2Off } from 'lucide-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import 'react-tooltip/dist/react-tooltip.css';
import DataTable from '../../../components/tables/DataTable';
import StatusBadge from '../../../components/StatusBadge';
import { listStoreApiTokens, issueStoreApiToken, revokeStoreApiToken } from '../../../utils/queries';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';

export default function ApiTokensTab({ storeId }) {
	const canEdit = useCan(PLATFORMADMIN_ROLES);
	const [tokens, setTokens] = useState([]);
	const [loading, setLoading] = useState(true);
	const [tokenName, setTokenName] = useState('');
	const [issuing, setIssuing] = useState(false);
	const [error, setError] = useState('');
	const [justIssued, setJustIssued] = useState(null);
	const [formOpen, setFormOpen] = useState(false);

	const refresh = () => {
		setLoading(true);
		return listStoreApiTokens(storeId).then((result) => {
			if (result?.success) setTokens(result.data);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [storeId]);

	const handleIssue = async (e) => {
		e.preventDefault();
		setIssuing(true);
		setError('');
		setJustIssued(null);
		const result = await issueStoreApiToken(storeId, tokenName);
		setIssuing(false);
		if (!result?.success) {
			setError(result?.error?.message || 'Failed to issue token.');
			return;
		}
		setJustIssued(result.data);
		setTokenName('');
		refresh();
	};

	const handleCancel = () => {
		setFormOpen(false);
		setTokenName('');
		setError('');
	};

	const handleRevoke = async (id) => {
		await revokeStoreApiToken(id);
		refresh();
	};

	const columns = [
		{ key: 'tokenName', header: 'Name' },
		{ key: 'tokenPrefix', header: 'Prefix' },
		{ key: 'lastUsedAt', header: 'Last Used' },
		{
			key: 'activeFlag',
			header: 'Status',
			render: (value) => <StatusBadge status={value ? 'active' : 'inactive'} />,
		},
		...(canEdit
			? [
					{
						key: 'storeApiTokenId',
						header: '',
						sortable: false,
						render: (id, row) =>
							row.activeFlag ? (
								<button
									type='button'
									onClick={() => handleRevoke(id)}
									data-tooltip-id='api-token-actions-tooltip'
									data-tooltip-content='Revoke this token, making it unusable'
								>
									<Link2Off size={20} />
								</button>
							) : null,
					},
				]
			: []),
	];

	const emptyHeadingContent = canEdit && !formOpen ? (
		<>
			No API tokens yet{' '}
			<button
				type='button'
				className='underline text-black hover:no-underline'
				onClick={() => setFormOpen(true)}
			>
				Add a token
			</button>
		</>
	) : (
		'No API tokens yet'
	);

	return (
		<div className='max-w-5xl'>
			{justIssued && (
				<div className='mb-5 p-4 border border-green-200 bg-green-50 text-sm'>
					<p className='font-medium text-green-800 normal-case mb-1'>
						Token created - copy it now, it will not be shown again:
					</p>
					<code className='block break-all text-xs'>{justIssued.token}</code>
				</div>
			)}

			<DataTable columns={columns} data={tokens} loading={loading} emptyHeading={emptyHeadingContent} />

			{canEdit && !formOpen && tokens.length > 0 && (
				<button
					type='button'
					className='btn-secondary-small mt-5 rounded-full p-1.5'
					aria-label='Add'
					onClick={() => setFormOpen(true)}
				>
					<Plus className='size-4' />
				</button>
			)}

			{canEdit && formOpen && (
				<form onSubmit={handleIssue} className='flex items-end gap-3 mt-5'>
					<div className='flex flex-col'>
						<label>Token Name</label>
						<input
							className='form-input w-56'
							required
							value={tokenName}
							onChange={(e) => setTokenName(e.target.value)}
						/>
					</div>
					{error && <div className='text-sm text-pi-red'>{error}</div>}
					<button type='submit' disabled={issuing} className='btn-primary h-10'>
						{issuing ? 'Issuing...' : 'Issue Token'}
					</button>
					<button type='button' className='btn-secondary h-10' onClick={handleCancel}>
						Cancel
					</button>
				</form>
			)}

			<ReactTooltip id='api-token-actions-tooltip' />
		</div>
	);
}
